import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, PawPrint } from "lucide-react";
import { HUISDIEREN, waLink } from "@/data/site";
import CTASection from "@/components/CTASection";
import PackagesGrid from "@/components/PackagesGrid";

const Huisdieren = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative h-[85vh] overflow-hidden">
        <img
          src={HUISDIEREN.hero}
          alt="Huisdierenfotografie"
          className="absolute inset-0 w-full h-full object-cover animate-ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/20 to-black/70" />
        <div className="relative z-10 h-full flex flex-col justify-end pb-20 md:pb-28 container-x">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="eyebrow text-gold mb-4"
          >
            Trouwe vrienden, bijzondere herinneringen
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.15 }}
            className="font-serif text-white text-6xl md:text-7xl lg:text-8xl leading-[1.02] max-w-3xl"
          >
            <span className="italic font-light">Huisdieren.</span>
            <br />
            Voor altijd vastgelegd.
          </motion.h1>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-white py-24 md:py-32">
        <div className="container-narrow grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-7">
            <p className="font-serif text-2xl md:text-3xl leading-snug">
              {HUISDIEREN.intro}
            </p>
            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {HUISDIEREN.highlights.map((h) => (
                <li key={h} className="flex items-start gap-2 text-sm text-neutral-700">
                  <Check size={14} className="text-gold mt-1 shrink-0" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
          <aside className="md:col-span-5 bg-beige p-8 md:p-10">
            <p className="eyebrow mb-3">Vanaf</p>
            <p className="font-serif text-4xl md:text-5xl mb-2">€145</p>
            <p className="text-sm text-neutral-600 leading-relaxed">
              Elk pakket wordt op maat samengesteld. Contacteer me voor een persoonlijke offerte.
            </p>
            <a
              data-testid="service-book-btn-huisdieren"
              href={waLink("Hallo Eric, ik wil graag mijn huisdier shoot plannen.")}
              target="_blank"
              rel="noreferrer"
              className="btn-gold w-full mt-8"
            >
              Plan jouw fotoshoot
            </a>
            <Link
              to="/contact"
              className="block text-center text-xs uppercase tracking-[0.25em] mt-4 text-neutral-600 hover:text-gold transition"
            >
              of stuur een bericht
            </Link>
          </aside>
        </div>
      </section>

      {/* Image row */}
      <section className="bg-beige pt-4 pb-16 md:pb-20">
        <div className="container-x">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-5 max-w-5xl mx-auto">
            {HUISDIEREN.images.map((src, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="aspect-[4/5] overflow-hidden bg-neutral-100"
              >
                <img src={src} alt="Huisdierenfotografie" loading="lazy" className="w-full h-full object-cover" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="bg-beige pb-24 md:pb-32">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <PawPrint size={22} className="mx-auto mb-3 text-gold" />
            <p className="eyebrow mb-3">Pakketten</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              Altijd samen met <span className="italic text-gold">baasje(s)</span>
            </h2>
            <p className="mt-4 text-neutral-600 text-sm max-w-lg mx-auto leading-relaxed">
              Voor honden, katten én paarden — jij en jouw viervoeter samen in beeld.
            </p>
          </div>
          <PackagesGrid packages={HUISDIEREN.packages} serviceName="Huisdieren" />
          {HUISDIEREN.note && (
            <p className="mt-10 text-center text-sm text-neutral-600 max-w-2xl mx-auto italic">
              {HUISDIEREN.note}
            </p>
          )}
        </div>
      </section>

      {/* Extra options moved to the site-wide Opties page */}

      <CTASection />
    </>
  );
};

export default Huisdieren;
